// Work time → Dashboard. Per tracked person: month-to-date expected vs worked, carry-over
// from last month + balance (red/green), plus a today snapshot, upcoming planned absences,
// and the monthly accountant/manager export. Admin only.
import React, { useEffect, useState, useCallback } from 'react';
import { authFetch, API } from './api';
import HelpPanel from './HelpPanel';

interface WorkerRow {
  username: string; display_name: string; worker_type: string;
  expected_mtd_min: number; worked_mtd_min: number; net_mtd_min: number;
  expected_month_min: number; carry_in_min: number; balance_min: number;
  worked_days: number; absent_days: number; late_days: number; vacation_days: number;
  // Day ledger — separate from hours on purpose: extra hours never manufacture a missed day.
  expected_days: number; counted_days: number; paid_absence_days: number;
  missed_days: number; extra_days: number; present_days: number;
  absorbed_min: number; reliability: number | null; commitment: number | null;
  day_threshold_min: number;
  // Contract shape. Hours-only people (monthly hours / on call) have no day ledger at all.
  contract_type: string; day_accounting: boolean;
  monthly_target_min: number; monthly_progress: number | null;
  by_reason_net: Record<string, number>; color: 'green' | 'red';
  adjustments: { id: number; method: string; minutes: number; note: string | null }[];
}
interface Snapshot { username: string; display_name: string; worker_type: string;
  status: string; flag: string | null; has_activity: boolean; expected_min: number; actual_start: string | null; }
interface Upcoming { username: string; display_name: string; date: string; status: string; status_label: string; }
interface Expiring { username: string; display_name: string; kind: string; date: string; days_away: number; contract_type: string; }
interface SetupGap { username: string; display_name: string; contract_type: string; gap: string; }
interface DashData { year: number; month: number; today: string; expiring?: Expiring[];
  setup_gaps?: SetupGap[];
  workers: WorkerRow[]; today_snapshot: Snapshot[]; upcoming: Upcoming[]; reason_labels: Record<string, string>; }

const GAP_TEXT: Record<string, string> = {
  no_agreed_dates: 'is on agreed dates, but none are set for this month — no hours are expected of them at all',
  no_monthly_hours: 'is on a monthly-hours contract with no monthly total set — no hours are expected of them at all',
};

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

function fmtH(m: number | null) {
  if (m == null) return '—';
  const sign = m < 0 ? '-' : '';
  const a = Math.abs(m); const h = Math.floor(a / 60); const mm = Math.round(a % 60);
  return mm ? `${sign}${h}h ${mm}m` : `${sign}${h}h`;
}

const STATUS_LABEL: Record<string, string> = {
  working: 'Working', off: 'Off', holiday: 'Holiday', vacation: 'Vacation',
  sick_haigusleht: 'Sick (haigusleht)', sick_uncertified: 'Sick (no cert)',
  absent: 'Absent', day_off: 'Day off',
};

export default function WorkTimeDashboard() {
  const now = new Date();
  const [ym, setYm]   = useState({ year: now.getFullYear(), month: now.getMonth() + 1 });
  const [data, setData] = useState<DashData | null>(null);
  const [loading, setLoading] = useState(false);
  const [resolveFor, setResolveFor] = useState<string | null>(null);
  const [exporting, setExporting] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await authFetch(`${API}/statistics/work-time/dashboard?year=${ym.year}&month=${ym.month}`).then(r => r.json());
      setData(d);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [ym]);
  useEffect(() => { load(); }, [load]);

  const shift = (delta: number) => setYm(p => {
    let m = p.month + delta, y = p.year;
    if (m < 1) { m = 12; y--; } if (m > 12) { m = 1; y++; }
    return { year: y, month: m };
  });

  const resolveCarry = async (w: WorkerRow, method: string) => {
    await authFetch(`${API}/statistics/work-time/carryover/zero`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username: w.username, year: ym.year, month: ym.month,
        method, minutes: -w.carry_in_min }),
    });
    setResolveFor(null);
    await load();
  };
  const undoAdj = async (id: number) => {
    await authFetch(`${API}/statistics/work-time/carryover/${id}`, { method: 'DELETE' });
    await load();
  };

  const doExport = async (audience: 'manager' | 'accountant' | 'absences') => {
    setExporting(audience);
    try {
      const resp = await authFetch(`${API}/statistics/work-time/export?year=${ym.year}&month=${ym.month}&audience=${audience}`);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `work_time_${audience}_${ym.year}-${String(ym.month).padStart(2,'0')}.xlsx`;
      a.click(); URL.revokeObjectURL(url);
    } finally { setExporting(null); }
  };

  const workers = data?.workers || [];
  const present = (data?.today_snapshot || []);

  return (
    <div className="space-y-6">
      {/* Month nav + exports */}
      <div className="flex flex-wrap items-center gap-3">
        <button onClick={() => shift(-1)} className="px-2.5 py-1.5 text-sm rounded-lg bg-gray-800 text-gray-400 hover:text-white">←</button>
        <div className="text-white font-semibold text-lg w-44 text-center">{MONTHS[ym.month - 1]} {ym.year}</div>
        <button onClick={() => shift(1)} className="px-2.5 py-1.5 text-sm rounded-lg bg-gray-800 text-gray-400 hover:text-white">→</button>
        {loading && <span className="text-gray-600 text-sm">Loading…</span>}
        <div className="ml-auto flex items-center gap-2">
          <button onClick={() => doExport('absences')} disabled={!!exporting}
            title="The simplified per-company absence table in the accountant’s own layout — dates away, no hours. This is the file to forward."
            className="px-3 py-2 bg-blue-700 hover:bg-blue-600 text-white text-sm rounded-lg disabled:opacity-40">
            {exporting === 'absences' ? 'Exporting…' : '↓ Absence table .xlsx'}
          </button>
          <button onClick={() => doExport('accountant')} disabled={!!exporting}
            title="That same absence table plus the hours sheet (expected, paid absence, net remaining deficits)."
            className="px-3 py-2 bg-gray-800 hover:bg-gray-700 text-gray-300 text-sm rounded-lg disabled:opacity-40">
            {exporting === 'accountant' ? 'Exporting…' : '↓ Accountant .xlsx'}
          </button>
          <button onClick={() => doExport('manager')} disabled={!!exporting}
            className="px-3 py-2 bg-emerald-700 hover:bg-emerald-600 text-white text-sm rounded-lg disabled:opacity-40">
            {exporting === 'manager' ? 'Exporting…' : '↓ Manager .xlsx'}
          </button>
        </div>
      </div>

      {/* Today snapshot */}
      <div>
        <div className="text-gray-500 text-xs uppercase tracking-widest font-semibold mb-2 px-1">Today</div>
        <div className="flex flex-wrap gap-2">
          {present.length === 0 && <span className="text-gray-600 text-sm px-1">No tracked workers.</span>}
          {present.map(s => {
            const isWork = s.status === 'working';
            const late = s.flag === 'late?' || s.flag === 'late';
            const cls = late ? 'bg-orange-950/40 text-orange-300 border-orange-900/50'
              : !isWork ? 'bg-gray-800 text-gray-500 border-gray-700'
              : s.has_activity ? 'bg-green-950/40 text-green-300 border-green-900/50'
              : 'bg-gray-800 text-gray-400 border-gray-700';
            const sub = !isWork ? (STATUS_LABEL[s.status] || s.status)
              : late ? `late · in ${s.actual_start || '—'}`
              : s.has_activity ? `in ${s.actual_start || ''}` : 'no activity yet';
            return (
              <div key={s.username} className={`px-3 py-1.5 rounded-lg border text-xs ${cls}`}>
                <span className="font-medium">{s.display_name}</span>
                <span className="opacity-70"> · {sub}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Progress table */}
      <div>
        <div className="text-gray-500 text-xs uppercase tracking-widest font-semibold mb-2 px-1">Month-to-date progress</div>
        <div className="bg-[#1a1d27] rounded-xl border border-gray-800 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-gray-500 text-xs border-b border-gray-800">
                <th className="px-3 py-2 text-left font-medium">Worker</th>
                <th className="px-3 py-2 text-right font-medium">Expected</th>
                <th className="px-3 py-2 text-right font-medium">Worked</th>
                <th className="px-3 py-2 text-right font-medium">Carry-in</th>
                <th className="px-3 py-2 text-right font-medium">Balance</th>
                <th className="px-3 py-2 text-center font-medium" title="Days that count, out of contracted days. A day counts only if the person worked at least the threshold AFTER any hours used to work back an earlier shortfall.">Days</th>
                <th className="px-3 py-2 text-center font-medium" title="Contracted days with no attendance and no paid absence">Missed</th>
                <th className="px-3 py-2 text-center font-medium" title="Approved paid absence (vacation, haigusleht). Never counted as unreliability.">Paid off</th>
                <th className="px-3 py-2 text-center font-medium">Late</th>
                <th className="px-3 py-2 text-left font-medium">Open reasons</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {workers.map(w => (
                <React.Fragment key={w.username}>
                  <tr className="border-b border-gray-800/50">
                    <td className="px-3 py-2.5 text-gray-200 whitespace-nowrap">{w.display_name}
                      <span className="ml-1.5 text-[9px] uppercase text-gray-600">{w.worker_type}</span></td>
                    <td className="px-3 py-2.5 text-right text-gray-300">{fmtH(w.expected_mtd_min)}</td>
                    <td className="px-3 py-2.5 text-right text-gray-300">
                      {fmtH(w.worked_mtd_min)}
                      {w.monthly_target_min > 0 && (
                        <>
                          <span className="text-gray-600 text-xs"> / {Math.round(w.monthly_target_min / 60)}h</span>
                          <div className="mt-1 h-1 w-16 ml-auto bg-gray-800 rounded overflow-hidden">
                            <div className={`h-full ${(w.monthly_progress ?? 0) >= 1 ? 'bg-green-500' : 'bg-blue-500'}`}
                              style={{ width: `${Math.min(100, Math.round((w.monthly_progress ?? 0) * 100))}%` }} />
                          </div>
                        </>
                      )}
                    </td>
                    <td className={`px-3 py-2.5 text-right ${w.carry_in_min < 0 ? 'text-red-400' : w.carry_in_min > 0 ? 'text-green-400' : 'text-gray-600'}`}>{fmtH(w.carry_in_min)}</td>
                    <td className="px-3 py-2.5 text-right">
                      <span className={`inline-flex items-center gap-1.5 font-semibold ${w.color === 'red' ? 'text-red-400' : 'text-green-400'}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${w.color === 'red' ? 'bg-red-400' : 'bg-green-400'}`} />
                        {fmtH(w.balance_min)}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-center">
                      {!w.day_accounting ? (
                        <span className="text-[10px] text-gray-600 uppercase"
                              title="Hours-only contract — days are not counted for this person">
                          hours only
                        </span>
                      ) : (
                      <span className={w.missed_days > 0 ? 'text-gray-300' : 'text-gray-400'}>
                        {w.counted_days}<span className="text-gray-600">/{w.expected_days}</span>
                      </span>
                      )}
                      {w.extra_days > 0 && (
                        <span className="ml-1 text-[10px] text-green-400" title="Days worked outside contracted days">
                          +{w.extra_days}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-center">
                      {w.day_accounting && w.missed_days > 0
                        ? <span className="text-red-400 font-medium">{w.missed_days}</span>
                        : <span className="text-gray-700">—</span>}
                    </td>
                    <td className="px-3 py-2.5 text-center">
                      {w.paid_absence_days > 0
                        ? <span className="text-blue-400">{w.paid_absence_days}</span>
                        : <span className="text-gray-700">—</span>}
                    </td>
                    <td className="px-3 py-2.5 text-center">{w.late_days > 0 ? <span className="text-orange-400">{w.late_days}</span> : <span className="text-gray-700">—</span>}</td>
                    <td className="px-3 py-2.5 text-xs text-gray-400">
                      {Object.keys(w.by_reason_net).length === 0 ? <span className="text-gray-700">—</span> :
                        Object.entries(w.by_reason_net).map(([k, v]) =>
                          <span key={k} className="inline-block mr-2 whitespace-nowrap">
                            {(data?.reason_labels?.[k] || k)}: <span className="text-gray-300">{fmtH(v)}</span>
                          </span>)}
                    </td>
                    <td className="px-3 py-2.5 text-right whitespace-nowrap">
                      {w.carry_in_min !== 0 && (
                        <button onClick={() => setResolveFor(resolveFor === w.username ? null : w.username)}
                          className="px-2.5 py-1 bg-gray-800 hover:bg-gray-700 text-gray-300 text-xs rounded-lg">
                          Resolve carry-over
                        </button>
                      )}
                    </td>
                  </tr>
                  {w.adjustments.length > 0 && (
                    <tr className="border-b border-gray-800/50">
                      <td colSpan={11} className="px-3 pb-2 pt-0">
                        <div className="flex flex-wrap gap-2">
                          {w.adjustments.map(a => (
                            <span key={a.id} className="inline-flex items-center gap-1.5 text-[11px] bg-gray-800 text-gray-400 rounded px-2 py-0.5">
                              {a.method.replace('_', ' ')} {fmtH(a.minutes)}
                              <button onClick={() => undoAdj(a.id)} className="text-gray-500 hover:text-red-300">×</button>
                            </span>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                  {resolveFor === w.username && (
                    <tr className="border-b border-gray-800/50 bg-gray-900/40">
                      <td colSpan={11} className="px-3 py-3">
                        <div className="flex flex-wrap items-center gap-2 text-xs">
                          <span className="text-gray-400">Resolve {fmtH(w.carry_in_min)} for {w.display_name}:</span>
                          <button onClick={() => resolveCarry(w, 'vacation')}
                            className="px-2.5 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded-lg">Cover with vacation</button>
                          <button onClick={() => resolveCarry(w, 'write_off')}
                            className="px-2.5 py-1 bg-gray-700 hover:bg-gray-600 text-gray-200 rounded-lg">Write off (unpaid)</button>
                          <button onClick={() => resolveCarry(w, 'carry_forward')}
                            className="px-2.5 py-1 bg-gray-700 hover:bg-gray-600 text-gray-200 rounded-lg">Carry forward</button>
                          <button onClick={() => setResolveFor(null)} className="text-gray-500 hover:text-gray-300 ml-1">Cancel</button>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
              {workers.length === 0 && !loading && (
                <tr><td colSpan={11} className="px-3 py-6 text-center text-gray-600 text-sm">No tracked workers. Mark people as tracked in the Workforce tab.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-gray-600 mt-2">
          Expected is always the contractual schedule. Balance = carry-in + (worked − expected) so far this month.
          Green = on track or ahead, red = behind. Weekend/overtime hours work off deficits automatically.
        </p>
      </div>

      {/* Upcoming absences */}
      <div>
        <div className="text-gray-500 text-xs uppercase tracking-widest font-semibold mb-2 px-1">Upcoming planned absences (next 14 days)</div>
        <div className="bg-[#1a1d27] rounded-xl border border-gray-800 p-3">
          {(data?.upcoming || []).length === 0 ? (
            <span className="text-gray-600 text-sm">None planned.</span>
          ) : (
            <div className="flex flex-wrap gap-2">
              {data!.upcoming.map((u, i) => (
                <span key={i} className="text-xs bg-gray-800 text-gray-300 rounded-lg px-2.5 py-1">
                  {u.display_name} · <span className="text-gray-500">{u.status_label}</span> · {u.date}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* A contract expecting nothing all month is a half-finished setup, and it is invisible
          otherwise: the person just shows zeros and looks fine. Red, not amber — this one is
          silently wrong rather than merely due. */}
      {(data?.setup_gaps?.length ?? 0) > 0 && (
        <div className="mb-5 bg-[#1a1d27] border border-red-900/50 rounded-xl p-4">
          <div className="text-red-400/90 text-xs uppercase tracking-widest font-semibold mb-2">
            Expecting nothing
          </div>
          <div className="space-y-1.5">
            {data!.setup_gaps!.map((g, i) => (
              <div key={i} className="flex items-baseline gap-2 text-sm flex-wrap">
                <span className="text-gray-200">{g.display_name}</span>
                <span className="text-xs text-gray-500">{GAP_TEXT[g.gap] || g.gap}</span>
              </div>
            ))}
          </div>
          <p className="text-xs text-gray-600 mt-2.5">
            Set this up in Workforce → Terms, or on the Calendar tab for agreed dates. Until then
            they accrue no expected hours and every figure below reads zero for them.
          </p>
        </div>
      )}

      {/* Contract and probation ends need attention BEFORE they arrive, so they sit up here
          rather than inside a drawer. Window: 7 days behind to 30 ahead. */}
      {(data?.expiring?.length ?? 0) > 0 && (
        <div className="mb-5 bg-[#1a1d27] border border-amber-900/40 rounded-xl p-4">
          <div className="text-amber-400/90 text-xs uppercase tracking-widest font-semibold mb-2">
            Ending soon
          </div>
          <div className="space-y-1.5">
            {data!.expiring!.map((e, i) => (
              <div key={i} className="flex items-center gap-3 text-sm flex-wrap">
                <span className="text-gray-200 min-w-[150px]">{e.display_name}</span>
                <span className="text-xs text-gray-500">
                  {e.kind === 'probation_end' ? 'Probation ends' : 'Contract ends'} {e.date}
                </span>
                <span className={`text-xs ${e.days_away < 0 ? 'text-red-400' : e.days_away <= 7 ? 'text-amber-400' : 'text-gray-500'}`}>
                  {e.days_away < 0 ? `${-e.days_away} day(s) ago` :
                   e.days_away === 0 ? 'today' : `in ${e.days_away} day(s)`}
                </span>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-gray-600 mt-2">
            Extend or end these under Work time → Workforce → Terms.
          </p>
        </div>
      )}

      <HelpPanel items={[
        { label: '↓ Absence table .xlsx', desc: 'The sheet the accountant asked for: one block per company, and per person the DATES they were away in three columns (Haigusleht / hooldusleht, Puhkus, Puudumine). No hours. Vacation shows its real start and end even when they fall in another month; the other columns are month-only. The bracketed number is contracted days missed in this month.' },
        { label: '↓ Accountant / Manager .xlsx', desc: 'The same absence table, plus the hours sheets behind it. Note the two can look like they disagree: the absence table lists every date away, while the hours sheet nets off time that was later worked back. Both are correct — one is a record of days, the other of hours owed.' },
        { label: 'Who is on the absence table', desc: 'Only people whose employment terms name a company. Anyone without one is listed by name at the bottom of the sheet instead, so nobody drops off a payroll hand-off silently. Set it in Workforce → Terms.' },
        { label: 'Ending soon', desc: 'Contracts and probation periods finishing within the next 30 days (or just missed). A temp whose contract has lapsed keeps accruing expected hours until you end or extend it.' },
        { label: 'Expecting nothing', desc: 'Someone whose contract generates no expected hours for the whole month — almost always a setup that was started and not finished, such as moving a person to agreed dates without then agreeing any. They quietly show zeros and look fine, which is why it is called out here rather than left to be noticed.' },
        { label: 'Absent', desc: 'Unpaid away-days only: absent, day off, sick without a certificate, unpaid leave. Approved paid leave is NOT counted here — vacation and haigusleht appear under Paid off, so agreed time off is never reported as absence.' },
        { label: 'hours only', desc: 'Shown instead of a day count for people on monthly-hours or on-call contracts. They are measured in hours, so the day threshold and the missed-day count do not apply to them.' },
        { label: 'Worked … / 60h bar', desc: 'For monthly-hours contracts: progress towards the agreed monthly total. The bar turns green once the target is met.' },
        { label: 'Days (x/y)', desc: 'Days that COUNT out of contracted days. A day counts only if the person worked at least the threshold set in Workforce, measured AFTER any hours used to work back an earlier shortfall — so eight days of overtime can never cover up one missed day. A green +n means days worked outside their contracted days.' },
        { label: 'Missed', desc: 'Contracted days with neither attendance nor approved paid absence. This is the number hours alone will hide: someone can be positive on hours and still have missed a day of cover.' },
        { label: 'Paid off', desc: 'Approved paid absence (vacation, haigusleht). Deliberately kept separate from Missed — paid leave is not unreliability.' },
        { label: '← / → (month)', desc: 'Move between months. Everything below reflects the selected month; the balance also includes carry-over from the month before it.' },
        { label: 'Today strip', desc: 'Each tracked worker\'s state right now: green = clocked in (has activity), orange = arrived late, grey = off / no activity yet. Off-day workers show their reason (vacation, sick, etc.).' },
        { label: 'Expected / Worked', desc: 'Month-to-date. Expected is the contractual schedule (Mon–Fri × shift minus red/short days) and never changes for a reason. Worked is what they actually did, including weekends.' },
        { label: 'Carry-in', desc: 'The balance carried over from the previous month. Negative (red) = they still owe hours; positive (green) = they have a buffer.' },
        { label: 'Balance', desc: 'Carry-in + (worked − expected) so far. Red means behind, green means on track or ahead. This is the number that carries into next month.' },
        { label: 'Open reasons', desc: 'Net missing hours by reason after weekend/overtime work has been used to pay them back — the same figures the accountant export shows.' },
        { label: 'Resolve carry-over', desc: 'Only shown when carry-in isn\'t zero. Cover with vacation (paid leave fills the gap), Write off (unpaid — a salary cut), or Carry forward (leave it to accumulate). Each resolution is listed under the row and can be undone with ×.' },
        { label: '↓ Accountant / Manager .xlsx', desc: 'Month hand-off. Accountant = one sheet with net hours-not-worked by reason. Manager = that same sheet plus a full-detail sheet (gross sick/absent, worked-back, net) — one download.' },
      ]} />
    </div>
  );
}
